"use client";

import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { NAV_LINKS, MAISON } from "@/lib/diwline-data";

/** Monogramme royal D couronné — logo DIW'LINE */
export function CrownMonogram({ size = 40 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden className="shrink-0">
      <defs>
        <linearGradient id="orGrad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#f5e3a3" />
          <stop offset="55%" stopColor="#d4af37" />
          <stop offset="100%" stopColor="#8c6d1f" />
        </linearGradient>
      </defs>
      <circle cx="24" cy="24" r="22.5" fill="none" stroke="url(#orGrad)" strokeWidth="1.4" />
      <circle cx="24" cy="24" r="19.5" fill="none" stroke="url(#orGrad)" strokeWidth="0.5" opacity="0.6" />
      <path
        d="M12 19 l4 5 l4 -8 l4 8 l4 -8 l4 8 l4 -5 v6.5 h-24 z"
        fill="none"
        stroke="url(#orGrad)"
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
      <circle cx="16" cy="17.5" r="1.1" fill="url(#orGrad)" />
      <circle cx="24" cy="15.5" r="1.1" fill="url(#orGrad)" />
      <circle cx="32" cy="17.5" r="1.1" fill="url(#orGrad)" />
      <text
        x="24"
        y="37"
        textAnchor="middle"
        fontFamily="Georgia, serif"
        fontStyle="italic"
        fontSize="12.5"
        fill="url(#orGrad)"
      >
        D
      </text>
    </svg>
  );
}

export function RoyalNavbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [actif, setActif] = useState("#accueil");

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 40);
      const ids = NAV_LINKS.map(l => l.href.slice(1));
      for (const id of ids) {
        const el = document.getElementById(id);
        if (el) {
          const r = el.getBoundingClientRect();
          if (r.top <= 120 && r.bottom > 120) {
            setActif(`#${id}`);
            break;
          }
        }
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-royal/92 backdrop-blur-md border-b border-gold/20 shadow-[0_10px_40px_rgba(0,0,0,0.45)]"
          : "bg-gradient-to-b from-black/60 to-transparent"
      }`}
    >
      <nav className="mx-auto flex max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8 h-16 md:h-20" aria-label="Navigation royale">
        <a href="#accueil" className="flex items-center gap-3 group" aria-label="DIW'LINE — Accueil">
          <CrownMonogram size={40} />
          <span className="leading-tight">
            <span className="block font-display text-lg md:text-xl font-bold tracking-[0.18em] text-gold-gradient animate-shimmer">
              DIW&apos;LINE
            </span>
            <span className="block text-[9px] md:text-[10px] uppercase tracking-[0.32em] text-cream/60">
              Huilleries Royales · 1958
            </span>
          </span>
        </a>

        <ul className="hidden lg:flex items-center gap-1">
          {NAV_LINKS.map(link => (
            <li key={link.href}>
              <a
                href={link.href}
                className={`relative px-3 py-2 text-[13px] tracking-wide transition-colors hover:text-gold ${
                  actif === link.href ? "text-gold" : "text-cream/80"
                }`}
              >
                {link.label}
                <span
                  className={`absolute left-3 right-3 -bottom-0.5 h-px bg-gold transition-transform duration-300 origin-center ${
                    actif === link.href ? "scale-x-100" : "scale-x-0"
                  }`}
                />
              </a>
            </li>
          ))}
        </ul>

        <div className="hidden lg:block">
          <a
            href="#produits"
            className="whitespace-nowrap rounded-sm border border-gold/60 bg-gold/10 px-5 py-2.5 text-[13px] font-semibold tracking-wider text-gold uppercase transition-all hover:bg-gold hover:text-royal"
          >
            La Gamme Prestige
          </a>
        </div>

        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="lg:hidden inline-flex items-center justify-center h-11 w-11 rounded-sm border border-gold/40 text-gold"
          aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
          aria-expanded={open}
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </nav>

      {open && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          className="lg:hidden border-t border-gold/15 bg-royal/97 backdrop-blur-lg"
        >
          <ul className="px-6 py-4 space-y-1">
            {NAV_LINKS.map(link => (
              <li key={link.href}>
                <a
                  href={link.href}
                  onClick={() => setOpen(false)}
                  className={`block px-3 py-3 rounded-sm text-sm tracking-widest uppercase ${
                    actif === link.href ? "text-gold bg-gold/10" : "text-cream/85"
                  }`}
                >
                  {link.label}
                </a>
              </li>
            ))}
          </ul>
          <p className="px-6 pb-5 font-elegant italic text-xs text-gold/70">{MAISON.devise}</p>
        </motion.div>
      )}
    </header>
  );
}
